import { useEffect, useMemo, useState } from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import {
  addSegmentDefinition,
  addSegmentValue,
  editSegmentDataDefinition,
  getSegmentDefinition,
  getSegmentDefinitionData,
  getTotalSegmentValues,
} from '../../../api/segmentDefinition';
import { Button, Layout, Pagination, Select, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { usePagination } from '../../../common/hooks/data';
import { ACTION_EDIT, ACTION_NEW } from '../../../constants/gl-configuration';
import { apiErrorHandler, successHandler } from '../../../utils/handlers';
import { asyncDebounce } from '../../../utils/utils';
import AddAccountTypeModal from '../common/NewAccountTypeModal';
import AddSegmentValueModal from './addSegmentValue';

const SegmentDefinition = () => {
  const { page, limit, setPage, setLimit } = usePagination();
  const [totalResults, setTotalResults] = useState(0);
  const [isAddSegmentModalOpen, setIsAddSegmentModalOpen] = useState(false);
  const [isAddSegmentValueModalOpen, setIsAddSegmentValueModalOpen] = useState(false);
  const [isAddSegmentValueEditModalOpen, setIsAddSegmentValueEditModalOpen] = useState(false);
  const [editableSegmentValue, setEditableSegmentValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [segmentDropdownData, setSegementDropdownData] = useState(null);
  const [selection, setSelection] = useState(null);
  const [selectedSegmentData, setSelectedSegementData] = useState(null);

  const toggleAddSegmentModal = () => {
    setIsAddSegmentModalOpen(!isAddSegmentModalOpen);
  };

  const toggleAddSegmentValueModal = () => {
    setIsAddSegmentValueModalOpen(!isAddSegmentValueModalOpen);
  };

  const toggleAddSegmentValueEditModal = () => {
    setIsAddSegmentValueEditModalOpen(!isAddSegmentValueEditModalOpen);
  };

  const fetchSegmentDefinition = async (searchVal) => {
    setLoading(true);
    try {
      const params = {
        searchValue: searchVal || '',
      };

      const response = await getSegmentDefinition(params);
      const formatedResp = response.data.data.map((d) => ({
        value: d.accountSegmentId,
        label: d.title,
      }));
      setLoading(false);
      setSegementDropdownData(formatedResp);
      // this is required for search, loadOptions of Select expects return value
      return formatedResp;
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  useEffect(() => {
    fetchSegmentDefinition();
  }, []);

  const debounceApiCall = asyncDebounce(fetchSegmentDefinition, 1000);

  const handleSelectChange = async (selectedOption, triggeredAction) => {
    if (triggeredAction.action === 'clear') {
      setSelectedSegementData(null);
      fetchSegmentDefinition();
    } else {
      setPage(1);
      setLimit(5);
      fetchSelectedSegmentData({
        searchValue: selectedOption?.label,
        page: 1,
        limit: 5,
      });
      fetchSelectedSegmentDataCount(selectedOption);
    }
    setSelection(selectedOption);
  };

  const fetchSelectedSegmentData = async (payload) => {
    const params = {
      limit,
      page,
      searchValue: '',
      ...payload,
    };
    setLoading(true);
    try {
      const response = await getSegmentDefinitionData(params);
      setSelectedSegementData(response?.data?.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const debounceFetchSegmentData = asyncDebounce(fetchSelectedSegmentData, 1000);

  const fetchSelectedSegmentDataCount = async (selectedOption) => {
    const params = {
      searchValue: selectedOption?.label || '',
    };
    setLoading(true);
    try {
      const response = await getTotalSegmentValues(params);
      setTotalResults(response?.data?.data?.totalCount);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const handleSegmentCreate = async (values) => {
    setLoading(true);
    try {
      await addSegmentDefinition({ title: values.title });
      fetchSegmentDefinition();
      toggleAddSegmentModal();
      successHandler({
        message: {
          message: 'Segment Created Successfully',
          title: 'Success',
        },
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const handleSegmentValueCreate = async (values) => {
    setLoading(true);
    try {
      await addSegmentValue({
        accountSegmentId: selection?.value,
        segmentValue: values?.segmentValue,
        description: values?.description,
        accountTypeId: values?.accountType?.value || null,
      });
      toggleAddSegmentValueModal();
      fetchSelectedSegmentData({ searchValue: selection?.label });
      fetchSelectedSegmentDataCount(selection);
      successHandler({
        message: {
          message: 'Segment Value Created Successfully',
          title: 'Success',
        },
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const saveSegmentDataEdit = (item) => {
    setEditableSegmentValue(item);
    toggleAddSegmentValueEditModal();
  };

  const handleSegmentValueEdit = async (values) => {
    try {
      setLoading(true);
      await editSegmentDataDefinition({
        accountSegmentDataId: editableSegmentValue?.accountSegmentDataId,
        accountSegmentId: selection?.value,
        // not Editable Hence Changes are Not Accounted
        segmentValue: editableSegmentValue?.segmentValue,
        description: values?.description,
        accountTypeId: values?.accountType?.value || null,
      });
      toggleAddSegmentValueEditModal();
      fetchSelectedSegmentData({ searchValue: selection?.label });
      successHandler({
        message: {
          message: 'Segment Value Updated Successfully',
          title: 'Success',
        },
      });
      setLoading(false);
    } catch (err) {
      setLoading(false);
      apiErrorHandler(err);
    }
  };

  const getTableHeaders = () => {
    const headers = ['NUMBER', 'DESCRIPTION', 'ACCOUNT TYPE', ''];
    if (selection?.value !== 1) {
      headers.splice(2, 1);
    }
    return headers;
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Layout>
      <div className="atd-div atd-div-1">
        <div className="atd-block my-5">
          <div className="atd-block-heading">Segment Definition</div>
          <div className="segment-btns">
            <Button color="primary" className="atd-btn" size="xs" onClick={toggleAddSegmentModal}>
              Add Segment
            </Button>
            <Button
              color="primary"
              className="atd-btn"
              size="xs"
              onClick={toggleAddSegmentValueModal}
              disabled={!selection}
            >
              Add Segment Value
            </Button>
          </div>
          <hr />
          <div className="atd-block-heading my-2">Segment</div>
          <div className="atd-input-segment">
            <div className="common-local-search w-100">
              <i className="fas fa-search" />
              <Select
                name="segmentDefinitionDropdown"
                formInput={false}
                classNamePrefix="select-dropdown"
                placeholder="Choose An account Type "
                onChange={handleSelectChange}
                value={selection}
                size="lg"
                isClearable
                cacheOptions
                isAsync
                loadOptions={debounceApiCall}
                defaultOptions={segmentDropdownData}
              />
            </div>
          </div>
        </div>
        {selectedSegmentData?.length > 0 && (
          <div className="segment-table">
            <Table headers={getTableHeaders()} bordered responsive>
              <tbody className="segment-tbody">
                {selectedSegmentData?.map((item) => (
                  <tr key={item?.accountSegmentDataId}>
                    <td>{item?.segmentValue}</td>
                    <td className="seg-description-td">{item?.description}</td>
                    {selection?.value === 1 && <td>{item?.accountType}</td>}
                    <td>
                      <FaPencilAlt color="#2173AE" onClick={() => saveSegmentDataEdit(item)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="pagination-araging-report">
              <Pagination
                totalResults={totalResults}
                page={page}
                limit={limit}
                setPage={(pageNo) => {
                  setPage(pageNo);
                  debounceFetchSegmentData({
                    searchValue: selection?.label,
                    page: pageNo,
                  });
                }}
                setLimit={(rowlimit) => {
                  setLimit(rowlimit);
                  setPage(1);
                  debounceFetchSegmentData({
                    searchValue: selection?.label,
                    limit: rowlimit,
                    page: 1,
                  });
                }}
              />
            </div>
          </div>
        )}
        <AddAccountTypeModal
          isOpen={isAddSegmentModalOpen}
          toggle={toggleAddSegmentModal}
          onSubmit={handleSegmentCreate}
          section="Segment"
          action={ACTION_NEW}
        />
        <AddSegmentValueModal
          isOpen={isAddSegmentValueModalOpen}
          toggle={toggleAddSegmentValueModal}
          onSubmit={handleSegmentValueCreate}
          action={ACTION_NEW}
          segment={selection}
        />
        <AddSegmentValueModal
          isOpen={isAddSegmentValueEditModalOpen}
          toggle={toggleAddSegmentValueEditModal}
          onSubmit={handleSegmentValueEdit}
          selectedValue={editableSegmentValue}
          action={ACTION_EDIT}
          segment={selection}
        />
      </div>
    </Layout>
  );
};

export default SegmentDefinition;
